import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CheckCircle, Clock, Trophy, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { rewardsService, WinnerClaimStatus } from '@/services/rewards.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface WinnersTableProps {
  rewardId: string;
  rewardTitle: string;
}

export function WinnersTable({ rewardId, rewardTitle }: WinnersTableProps) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['reward-winners', rewardId],
    queryFn: () => rewardsService.getWinners(rewardId),
  });

  // Mark as claimed mutation
  const markClaimedMutation = useMutation({
    mutationFn: (winnerId: string) =>
      rewardsService.markWinnerClaimed(winnerId),
    onSuccess: () => {
      toast.success('Winner marked as claimed');
      queryClient.invalidateQueries({ queryKey: ['reward-winners', rewardId] });
    },
    onError: () => {
      toast.error('Failed to update claim status');
    },
  });

  // Mark as unclaimed mutation
  const markUnclaimedMutation = useMutation({
    mutationFn: (winnerId: string) =>
      rewardsService.markWinnerUnclaimed(winnerId),
    onSuccess: () => {
      toast.success('Winner marked as unclaimed');
      queryClient.invalidateQueries({ queryKey: ['reward-winners', rewardId] });
    },
    onError: () => {
      toast.error('Failed to update claim status');
    },
  });

  const getClaimStatusBadge = (status: WinnerClaimStatus) => {
    switch (status) {
      case WinnerClaimStatus.Claimed:
        return (
          <Badge variant="success" className="flex items-center gap-1 w-fit">
            <CheckCircle className="h-3 w-3" />
            Claimed
          </Badge>
        );
      case WinnerClaimStatus.Unclaimed:
        return (
          <Badge variant="warning" className="flex items-center gap-1 w-fit">
            <Clock className="h-3 w-3" />
            Unclaimed
          </Badge>
        );
      case WinnerClaimStatus.Expired:
        return (
          <Badge
            variant="destructive"
            className="flex items-center gap-1 w-fit"
          >
            <XCircle className="h-3 w-3" />
            Expired
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getRankBadge = (rank: number) => {
    if (rank === 1) {
      return (
        <Badge className="bg-yellow-500 hover:bg-yellow-600 text-white">
          🥇 {rank}
        </Badge>
      );
    } else if (rank === 2) {
      return (
        <Badge className="bg-gray-400 hover:bg-gray-500 text-white">
          🥈 {rank}
        </Badge>
      );
    } else if (rank === 3) {
      return (
        <Badge className="bg-orange-600 hover:bg-orange-700 text-white">
          🥉 {rank}
        </Badge>
      );
    } else {
      return <Badge variant="outline">{rank}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Winners: {rewardTitle}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Stats */}
          {data && (
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>Total Winners: {data.length}</span>
              <span>
                Claimed:{' '}
                {
                  data.filter(
                    (w) => w.claimStatus === WinnerClaimStatus.Claimed,
                  ).length
                }
              </span>
              <span>
                Unclaimed:{' '}
                {
                  data.filter(
                    (w) => w.claimStatus === WinnerClaimStatus.Unclaimed,
                  ).length
                }
              </span>
            </div>
          )}

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rank</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Tokens Bid</TableHead>
                  <TableHead className="text-right">Lottery #</TableHead>
                  <TableHead>Won At</TableHead>
                  <TableHead>Claim Status</TableHead>
                  <TableHead>Claimed At</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      Loading winners...
                    </TableCell>
                  </TableRow>
                ) : !data?.length ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      No winners yet
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((winner) => (
                    <TableRow key={winner.id}>
                      <TableCell>{getRankBadge(winner.winningRank)}</TableCell>
                      <TableCell className="font-medium">
                        {winner.userName}
                      </TableCell>
                      <TableCell>{winner.userEmail}</TableCell>
                      <TableCell className="text-right font-medium">
                        {winner.tokensBid.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {winner.lotteryNumber ? (
                          <Badge variant="outline">
                            {winner.lotteryNumber}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {format(new Date(winner.wonAt), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        {getClaimStatusBadge(winner.claimStatus)}
                      </TableCell>
                      <TableCell>
                        {winner.claimedAt
                          ? format(
                              new Date(winner.claimedAt),
                              'MMM dd, yyyy HH:mm',
                            )
                          : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {winner.claimStatus === WinnerClaimStatus.Claimed ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              markUnclaimedMutation.mutate(winner.id)
                            }
                            disabled={markUnclaimedMutation.isPending}
                            title="Mark as Unclaimed"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              markClaimedMutation.mutate(winner.id)
                            }
                            disabled={markClaimedMutation.isPending}
                            title="Mark as Claimed"
                          >
                            <CheckCircle className="h-4 w-4 text-success" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
